import { useMemo, useState } from 'react';

import {
  useLegalClientBeneficiarySavePersonalData,
  useLegalClientCreateBeneficiary,
} from 'api/requests/onboarding/legal/beneficiary';
import { ErrorCode } from 'api/types/error';
import { useNotify } from 'libs/notify';
import {
  PersonalDataFormType,
  FormErrors,
} from 'modules/onboarding/common/forms/personalDataForm';

import { useOnboardingContext } from './useOnboardingContext';

export const usePersonalData = () => {
  const { goToBack, data, goToNext } = useOnboardingContext();
  const notify = useNotify();

  const {
    savePersonalData,
    loading: submitPersonalDataLoading,
    error: errorPersonalData,
  } = useLegalClientBeneficiarySavePersonalData();

  const { createBeneficiary, error: createError } =
    useLegalClientCreateBeneficiary();

  const [submitLoading, setSubmitLoading] = useState(false);

  const initialValues = useMemo<PersonalDataFormType | undefined>(
    () =>
      data.legalClientBeneficiary
        ? {
            email: data.legalClientBeneficiary.email,
            firstName: data.legalClientBeneficiary.firstName,
            lastName: data.legalClientBeneficiary.lastName,
            dateOfBirth: data.legalClientBeneficiary.dateOfBirth,
            middleName: data.legalClientBeneficiary.middleName,
          }
        : undefined,
    [data.legalClientBeneficiary]
  );

  const onSubmit = async (values: PersonalDataFormType) => {
    if (values) {
      if (!data.legalClientBeneficiary) {
        setSubmitLoading(true);

        try {
          const res = await createBeneficiary(data.legalClient.id!, values);
          if (res) {
            data.onCreateBeneficiary(res.id);
            goToNext();
          }
        } catch (error) {
          setSubmitLoading(false);
        }
      } else {
        const beneficiary = await savePersonalData(
          data.legalClient.id,
          data.legalClientBeneficiary.id,
          values
        );

        if (beneficiary) {
          goToNext();
        }
      }
    }
  };

  const formError = useMemo((): FormErrors | undefined => {
    if (createError?.code === ErrorCode.USER_WITH_EMAIL_ALREADY_EXISTS) {
      return { email: createError?.message };
    }

    if (errorPersonalData?.code === ErrorCode.USER_WITH_EMAIL_ALREADY_EXISTS) {
      return { email: errorPersonalData?.message };
    }

    if (createError || errorPersonalData) {
      notify.error(createError ?? errorPersonalData);
    }

    return undefined;
  }, [createError, errorPersonalData, notify]);

  return {
    onSubmit,
    goToBack,
    initialValues,
    formError,
    submitLoading: submitLoading || submitPersonalDataLoading,
  };
};
